/**
 * format.ts — small presentation helpers shared across the workspace columns.
 */

/**
 * Format a minor-unit euro amount (cents → EUR string), no decimals.
 * NOTE: our DTOs (totalSpendForCreator, priceAmountMinor) are minor units —
 * this matches the existing chatter-frontend behavior. Whole-EUR live `priceNet`
 * handling lives server-side, not here.
 */
export function formatEurMinor(amountMinor: number, maximumFractionDigits = 0): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits,
  }).format(amountMinor / 100);
}

/** Format a minor-unit amount with an explicit currency (2 dp). */
export function formatMinorAmount(minor: number, currency: string): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(minor / 100);
}

/**
 * Derive a two-character avatar monogram for a fan.
 * Prefers initials of the nickname/username; falls back to the first two
 * characters of the fan id (matching the design mockup, e.g. 8eff… → "8E").
 */
export function fanMonogram(opts: {
  nickname?: string | null;
  username?: string | null;
  fanId?: string | null;
}): string {
  const name = (opts.nickname || opts.username || '').trim();
  if (name) {
    const parts = name.split(/[\s_.-]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  if (opts.fanId) return opts.fanId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 2).toUpperCase();
  return '··';
}

/**
 * Human display name for a fan: nickname › Maloum username › "Fan".
 * Never a raw id (owner 2026-07-02) — internal ids are noise to chatters;
 * the avatar monogram/color still visually distinguishes unnamed rows.
 */
export function fanDisplayName(opts: {
  nickname?: string | null;
  username?: string | null;
  fanId?: string | null;
}): string {
  if (opts.nickname) return opts.nickname;
  if (opts.username) return opts.username;
  return 'Fan';
}

/**
 * Format a video duration in seconds → "m:ss" ("h:mm:ss" from one hour up).
 * Shared by the chat thread's media chips and the vault picker tiles.
 */
export function formatDuration(sec: number): string {
  const total = Math.max(0, Math.floor(sec));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}
